import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';
import { allArticles } from '@/mocks/articles';
import { Link } from 'react-router-dom';

export default function HeroSection() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [mobileArticlesOpen, setMobileArticlesOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 60);
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <header className="relative min-h-[620px] md:min-h-[720px] flex flex-col">
      {/* Navbar - always visible with background */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60' : 'bg-background-50/85 backdrop-blur-md border-b border-background-200/40'}`}>
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <span className="text-lg md:text-xl font-bold text-foreground-950 whitespace-nowrap tracking-tight">
              Hipobuy <span className="text-primary-500">Spreadsheet</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-5">
            <Link to="/" className="text-sm font-medium text-foreground-900 hover:text-primary-500 transition-colors whitespace-nowrap">
              Accueil
            </Link>
            <Link to={`/article/${allArticles[0].slug}`} className="text-sm font-medium text-foreground-800 hover:text-primary-500 transition-colors whitespace-nowrap">
              Guide Shopping
            </Link>
            <a href="#categories" className="text-sm font-medium text-foreground-800 hover:text-primary-500 transition-colors whitespace-nowrap">
              Catégories
            </a>
            <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="text-sm font-medium text-foreground-800 hover:text-primary-500 transition-colors whitespace-nowrap">
              Hipobuy Spreadsheet
            </a>
            <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="text-sm bg-primary-500 text-background-50 px-5 py-2.5 rounded-full font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              Buy It Now
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-950 cursor-pointer"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Menu"
          >
            <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-background-50 border-b border-background-200/60">
            <div className="flex flex-col">
              {/* Home */}
              <Link to="/" className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-900 hover:bg-background-100 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <i className="ri-home-4-line text-foreground-500 text-lg"></i>
                Accueil
              </Link>

              {/* Shopping Guide */}
              <Link to={`/article/${allArticles[0].slug}`} className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <i className="ri-book-open-line text-foreground-500 text-lg"></i>
                Guide Shopping
              </Link>

              {/* Categories expandable */}
              <div>
                <button
                  onClick={() => setMobileCatOpen(!mobileCatOpen)}
                  className="flex items-center justify-between w-full px-4 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <span className="flex items-center gap-3">
                    <i className="ri-price-tag-3-line text-foreground-500 text-lg"></i>
                    Catégories
                  </span>
                  {mobileCatOpen ? <i className="ri-arrow-up-s-line text-foreground-400"></i> : <i className="ri-arrow-down-s-line text-foreground-400"></i>}
                </button>
                {mobileCatOpen && (
                  <div className="bg-background-100/70 border-t border-background-200/50">
                    <div className="grid grid-cols-2 gap-0">
                      {categories.map((cat) => (
                        <a
                          key={cat.seq}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          className="px-4 py-2.5 text-xs text-foreground-600 hover:text-foreground-900 hover:bg-background-50 transition-colors whitespace-nowrap border-r border-b border-background-200/30"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {cat.name}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Articles expandable */}
              <div>
                <button
                  onClick={() => setMobileArticlesOpen(!mobileArticlesOpen)}
                  className="flex items-center justify-between w-full px-4 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <span className="flex items-center gap-3">
                    <i className="ri-article-line text-foreground-500 text-lg"></i>
                    Articles SEO
                  </span>
                  {mobileArticlesOpen ? <i className="ri-arrow-up-s-line text-foreground-400"></i> : <i className="ri-arrow-down-s-line text-foreground-400"></i>}
                </button>
                {mobileArticlesOpen && (
                  <div className="bg-background-100/70 border-t border-background-200/50">
                    {allArticles.map((article) => (
                      <Link
                        key={article.slug}
                        to={`/article/${article.slug}`}
                        className="block px-4 py-2.5 text-xs text-foreground-600 hover:text-foreground-900 hover:bg-background-50 transition-colors border-b border-background-200/30"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {article.title.length > 40 ? article.title.slice(0, 40) + '...' : article.title}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Buy It Now */}
              <div className="px-4 py-3 border-t border-background-200/60">
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center justify-center gap-2 w-full bg-primary-500 text-background-50 px-5 py-3 rounded-full font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <i className="ri-shopping-bag-line"></i>
                  Buy It Now
                </a>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero content */}
      <div className="flex-1 flex flex-col lg:flex-row pt-16 md:pt-18">
        {/* Left content */}
        <div className="w-full lg:w-[42%] flex flex-col justify-center px-4 md:px-6 lg:pl-10 xl:pl-16 py-10 md:py-14 lg:py-0">
          {/* Dropdown trigger */}
          <div className="mb-8" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="inline-flex items-center gap-3 px-5 py-3 rounded-full border border-background-300 bg-background-50 shadow-sm hover:border-foreground-300 transition-all cursor-pointer"
            >
              <span className="text-sm text-foreground-800 font-medium whitespace-nowrap">Explorer les catégories</span>
              {dropdownOpen ? (
                <i className="ri-arrow-up-s-line text-foreground-500 text-sm"></i>
              ) : (
                <i className="ri-arrow-down-s-line text-foreground-500 text-sm"></i>
              )}
            </button>

            {dropdownOpen && (
              <div className="absolute mt-2 w-72 bg-background-50/98 backdrop-blur-xl border border-background-200/70 rounded-2xl shadow-lg overflow-hidden z-50 max-h-[420px] overflow-y-auto">
                <div className="py-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.seq}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-5 py-3 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-accent-400 flex-shrink-0"></span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-5">
            Hipobuy Spreadsheet
            <br />
            <span className="text-foreground-700 font-normal">L&apos;outil n°1 pour analyser</span>
            <br />
            <span className="text-foreground-700 font-normal">vos données e-commerce</span>
          </h1>

          <p className="text-base md:text-lg text-foreground-600 mb-8 max-w-md leading-relaxed">
            Analysez, optimisez et automatisez vos performances produits avec le système Hipobuy Spreadsheet. La solution data-driven qui transforme vos chiffres en décisions rentables.
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center justify-center gap-2 bg-primary-500 text-background-50 px-7 py-3.5 rounded-full font-semibold text-sm hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              Hipobuy Spreadsheet
              <i className="ri-arrow-right-up-line text-base"></i>
            </a>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center justify-center gap-2 border-2 border-foreground-300 text-foreground-800 px-7 py-3.5 rounded-full font-semibold text-sm hover:border-foreground-500 hover:text-foreground-950 transition-all whitespace-nowrap cursor-pointer"
            >
              Buy It Now
              <i className="ri-shopping-bag-line text-base"></i>
            </a>
          </div>
        </div>

        {/* Right image */}
        <div className="w-full lg:w-[58%] relative min-h-[320px] md:min-h-[500px] lg:min-h-0 overflow-hidden">
          <div className="absolute inset-0 lg:rounded-l-[40px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20ecommerce%20entrepreneur%20analyzing%20colorful%20sales%20dashboard%20on%20laptop%20at%20bright%20modern%20workspace%2C%20variety%20of%20fashion%20products%20and%20accessories%20displayed%20on%20desk%2C%20dynamic%20startup%20atmosphere%2C%20natural%20window%20light%2C%20creative%20business%20energy%2C%20authentic%20entrepreneurial%20scene%2C%20editorial%20lifestyle%20photography&width=1400&height=900&seq=hero-main-v2&orientation=landscape"
              alt="Hipobuy Spreadsheet - Analyse de données e-commerce"
              title="Hipobuy Spreadsheet - Outil d'analyse de données e-commerce"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/30 to-transparent lg:hidden"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-background-50/60 via-transparent to-transparent lg:hidden"></div>
          </div>
        </div>
      </div>
    </header>
  );
}